/**
 * Concierge carousel item — "The Carousel Anchor".
 *
 * The Concierge doesn't render its own component on the page. Instead,
 * MainPage injects a SYNTHETIC bot into the home agent carousel and
 * pre-centers it, so the Concierge appears as the first/centered "agent
 * card". This file provides:
 *
 *   - `CONCIERGE_CAROUSEL_ID` — the synthetic bot id. MainPage special-cases
 *     this id everywhere a real chatbot id would be used (model fetch, select
 *     sync, start-chat) so it never hits chatbot loading. NEVER reuse this
 *     string for a real bot.
 *
 *   - `buildConciergeCarouselBot()` — returns a synthetic object shaped like
 *     the real carousel items MainPage maps over. The carousel reads:
 *       chatbot_id, chatbot_name, avatar_color, avatar_variant
 *     and feeds chatbot_name/avatar_color/avatar_variant into <BotAvatar>.
 *     Keeping the shape identical means the existing carousel render code needs
 *     no per-item type branching.
 *
 *   - `CONCIERGE_CARD_CLASS` — the className MainPage adds to the concierge
 *     carousel item so this file's dedicated CSS (`.concierge-carousel-card`)
 *     can style it differently from the real agent tiles.
 *
 * META-AGENT TREATMENT: the Concierge must instantly read as the platform's
 * front door — ABOVE the specialist agents — not as one more agent tile.
 * The `.css` does the heavy lifting (gradient ring, pulsing halo, badge, CTA
 * pill). MainPage renders the translated badge/CTA copy as real spans
 * (`.concierge-card-badge` / `.concierge-card-cta`) inside the concierge
 * card's JSX; the `.css` here styles and positions them.
 *
 * The synthetic-bot wiring and contract are FINAL and must be preserved.
 */

import './conciergeCarouselItem.css';

/** Synthetic id for the Concierge carousel card. Must never collide with a real chatbot_id. */
export const CONCIERGE_CAROUSEL_ID = '__concierge__';

/** Class MainPage applies to the concierge carousel item so this file's CSS targets it. */
export const CONCIERGE_CARD_CLASS = 'concierge-carousel-card';

/**
 * Shape of the synthetic carousel bot. Mirrors the fields MainPage's carousel
 * actually reads off a real bot — extend cautiously and keep it assignable to
 * the carousel's `any[]` bots array.
 */
export interface ConciergeCarouselBot {
  chatbot_id: typeof CONCIERGE_CAROUSEL_ID;
  chatbot_name: string;
  chatbot_description?: string;
  avatar_color?: string;
  avatar_variant?: number;
}

/**
 * Build the synthetic Concierge bot for the carousel. Kept hook-free: the
 * caller passes the translated display name (t('concierge.title')).
 */
export function buildConciergeCarouselBot(label: string): ConciergeCarouselBot {
  return {
    chatbot_id: CONCIERGE_CAROUSEL_ID,
    chatbot_name: label,
    // NOTE: no chatbot_description default — MainPage (the only call site)
    // always supplies t('concierge.greetingSub') on top of this spread.
    // 'blue' is one of the four TextureColor literals BotAvatar understands.
    // The CSS overrides --agent-halo with the brand-primary glow anyway, so
    // the texture color only affects the coin artwork itself.
    avatar_color: 'blue',
    avatar_variant: 0,
  };
}

/** True when a carousel bot is the synthetic Concierge item. */
export function isConciergeCarouselBot(bot: { chatbot_id?: string } | null | undefined): boolean {
  return !!bot && bot.chatbot_id === CONCIERGE_CAROUSEL_ID;
}
